import React, { useMemo, useRef, useState, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { z } from 'zod';

import { FormField, FormInput, FormSelect } from '@/components/form';
import { FormError } from '@/components/form/FormError';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useCreateCampaign } from '@/hooks/useCampaigns';
import { useConfigurations } from '@/hooks/useConfigurations';
import { useEffectiveOutcomes } from '@/hooks/useOutcomes';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReviewConfigs } from '@/hooks/useReviewConfigs';
import { useSettings } from '@/hooks/useSettings';
import { useTelnyxNumbers } from '@/hooks/useTelnyxNumbers';
import { useTwilioNumbers } from '@/hooks/useTwilioNumbers';
import { useWhatsAppNumbers } from '@/hooks/useWhatsAppNumbers';
import { useWhatsAppTemplates } from '@/hooks/useWhatsAppTemplates';
import { useWorkflows } from '@/hooks/useWorkflows';
import { scrollToFirstError, mergeApiErrors } from '@/lib/form-utils';
import { extractValidationErrors, extractErrorMessage } from '@/lib/utils';
import { convertUTCToLocalTime } from '@/utils/timezone';

import type { Contact, CreateCampaignDto } from '@repo/shared-types';
import type { ChangeEvent } from 'react';

interface LocalContact extends Contact {
  id: string;
  data?: Record<string, unknown> | null;
  _dataRaw?: string;
  _dataError?: string;
}

// Static fallback for retry triggers while the dynamic definitions load.
const RETRYABLE_OUTCOME_FALLBACK = ['NO_ANSWER', 'BUSY', 'VOICEMAIL', 'CANCELED', 'FAILED'];

// Form validation schema (excluding contacts which are handled separately)
const campaignFormSchema = z
  .object({
    name: z.string().min(2, 'Campaign name must be at least 2 characters'),
    runs_at: z.string().min(1, 'Run date is required'),
    configuration_id: z.string().min(1, 'Configuration is required'),
    channel: z.enum(['PSTN', 'WHATSAPP']),
    outbound_twilio_phone_number_id: z.string().optional(),
    outbound_telnyx_phone_number_id: z.string().optional(),
    default_review_config_id: z.string().optional(),
    workflow_id: z.string().optional(),
    // Retry behavior (defaults pulled from settings; editable per campaign)
    outcome_source: z.enum(['RAW', 'REVIEWED']).optional(),
    retryable_outcomes: z.array(z.string()).optional(),
    // WhatsApp campaign fields
    outbound_whatsapp_phone_number_id: z.string().optional(),
    wa_template_name: z.string().optional(),
    wa_mapping_header_image: z.string().optional(),
    wa_mapping_body: z.string().optional(),
    wa_mapping_button_url: z.string().optional(),
    wa_call_on_reply: z.boolean().optional(),
    wa_call_on_undelivered: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.channel === 'WHATSAPP' && !val.wa_template_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['wa_template_name'],
        message: 'Select a template for the WhatsApp campaign',
      });
    }
  });

type CampaignFormValues = z.infer<typeof campaignFormSchema>;

function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let current: Record<string, unknown> = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!(keys[i] in current) || typeof current[keys[i]] !== 'object') {
      current[keys[i]] = {};
    }
    current = current[keys[i]] as Record<string, unknown>;
  }
  current[keys[keys.length - 1]] = value;
}

function parseCellValue(value: string): unknown {
  if (value.includes('|')) {
    return value.split('|').map((v) => v.trim());
  }
  return value;
}

/** First word of a contact's name — what WhatsApp templates greet people by. */
function deriveFirstName(name: string | undefined | null): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

/**
 * Keep `data.first_name` in step with the Name column so template body vars can
 * map to it without the user hand-writing JSON per contact. A first_name the
 * user typed themselves is left alone: it is only rewritten when it is still
 * the value we derived from the previous name.
 */
function withDerivedFirstName(
  data: Record<string, unknown> | null | undefined,
  name: string,
  previousName?: string,
): Record<string, unknown> | undefined {
  const derived = deriveFirstName(name);
  const current = data?.first_name;
  const isOurs =
    current === undefined ||
    current === '' ||
    (previousName !== undefined && current === deriveFirstName(previousName));

  if (!isOurs) return data ?? undefined;
  if (!derived) {
    if (!data || current === undefined) return data ?? undefined;
    const { first_name: _dropped, ...rest } = data;
    return Object.keys(rest).length > 0 ? rest : undefined;
  }
  if (current === derived) return data ?? undefined;
  return { ...(data ?? {}), first_name: derived };
}

export const CampaignCreatePage = () => {
  usePageTitle('New Campaign');
  const navigate = useNavigate();
  const { data: configs = [], isLoading: configsLoading } = useConfigurations();
  const { data: reviewConfigs = [] } = useReviewConfigs();
  const { data: settings = [] } = useSettings();
  const { data: twilioNumbers = [] } = useTwilioNumbers();
  const { data: telnyxNumbers = [] } = useTelnyxNumbers();
  const { data: workflows = [] } = useWorkflows();
  const { data: whatsappNumbers = [] } = useWhatsAppNumbers();
  // Retry candidates come from the dynamic vocabulary (is_retryable flag).
  const { data: outcomeDefinitions } = useEffectiveOutcomes();
  const retryableOutcomeCandidates =
    outcomeDefinitions && outcomeDefinitions.length > 0
      ? outcomeDefinitions.filter((d) => d.is_retryable).map((d) => d.key)
      : RETRYABLE_OUTCOME_FALLBACK;
  const { data: whatsappTemplates = [] } = useWhatsAppTemplates();

  const createMutation = useCreateCampaign();
  const { toast } = useToast();

  const [contacts, setContacts] = useState<LocalContact[]>([]);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [expandedDataIds, setExpandedDataIds] = useState<Set<string>>(new Set());

  const toggleDataEditor = (id: string) => {
    setExpandedDataIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDataChange = (id: string, jsonString: string) => {
    setContacts((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        try {
          const parsed = JSON.parse(jsonString);
          return { ...c, data: parsed, _dataRaw: undefined, _dataError: undefined };
        } catch {
          return { ...c, _dataRaw: jsonString, _dataError: 'Invalid JSON' };
        }
      }),
    );
  };

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      name: '',
      runs_at: '',
      configuration_id: '',
      channel: 'PSTN',
      outbound_twilio_phone_number_id: '',
      outbound_telnyx_phone_number_id: '',
      default_review_config_id: '',
      workflow_id: '',
      outcome_source: 'REVIEWED',
      retryable_outcomes: [],
      outbound_whatsapp_phone_number_id: '',
      wa_template_name: '',
      wa_mapping_header_image: '',
      wa_mapping_body: '',
      wa_mapping_button_url: '',
      wa_call_on_reply: false,
      wa_call_on_undelivered: false,
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const configId = watch('configuration_id');
  const channel = watch('channel');
  const retryableOutcomes = watch('retryable_outcomes') || [];
  const retryDefaultsApplied = useRef(false);

  const toggleRetryableOutcome = (outcome: string) => {
    const current = watch('retryable_outcomes') || [];
    const next = current.includes(outcome)
      ? current.filter((o) => o !== outcome)
      : [...current, outcome];
    setValue('retryable_outcomes', next, { shouldDirty: true });
  };
  const approvedTemplates = useMemo(
    () => whatsappTemplates.filter((t) => t.status === 'APPROVED'),
    [whatsappTemplates],
  );

  // Set default config when configs load
  useEffect(() => {
    if (configs.length > 0 && !configId) {
      setValue('configuration_id', configs[0].id);
    }
  }, [configs, configId, setValue]);

  // Prefill retry defaults from the platform Campaign settings (once, on load).
  // These stay editable per campaign.
  useEffect(() => {
    if (retryDefaultsApplied.current || settings.length === 0) return;
    const src = settings.find((s) => s.key === 'CAMPAIGN_DEFAULT_OUTCOME_SOURCE')?.value;
    const outcomesCsv = settings.find(
      (s) => s.key === 'CAMPAIGN_DEFAULT_RETRYABLE_OUTCOMES',
    )?.value;
    if (src === 'RAW' || src === 'REVIEWED') setValue('outcome_source', src);
    if (outcomesCsv) {
      setValue(
        'retryable_outcomes',
        outcomesCsv
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      );
    }
    retryDefaultsApplied.current = true;
  }, [settings, setValue]);

  const selectedConfig = useMemo(
    () => configs.find((cfg) => cfg.id === configId),
    [configs, configId],
  );

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, { defval: '' });

      const getValue = (row: Record<string, string>, keys: string[]): string => {
        const normalizeKey = (k: string) => k.toLowerCase().replace(/[\s_-]/g, '');
        const normalizedKeys = keys.map(normalizeKey);

        for (const key of Object.keys(row)) {
          if (normalizedKeys.includes(normalizeKey(key))) {
            return row[key]?.toString().trim() ?? '';
          }
        }
        return '';
      };

      const formatPhone = (phone: string): string => {
        if (!phone) return '';
        return phone.replace(/\s/g, '');
      };

      const allColumns = json.length > 0 ? Object.keys(json[0]) : [];
      const dataColumns = allColumns.filter((col) => col.toLowerCase().startsWith('data.'));

      const parsedContacts = json
        .map((row) => {
          let data: Record<string, unknown> | undefined;
          if (dataColumns.length > 0) {
            data = {};
            for (const col of dataColumns) {
              const rawValue = row[col]?.toString().trim() ?? '';
              if (rawValue) {
                const dotPath = col.replace(/^data\./i, '');
                setNestedValue(data, dotPath, parseCellValue(rawValue));
              }
            }
            if (Object.keys(data).length === 0) data = undefined;
          }

          const name = getValue(row, ['name', 'fullname', 'full_name', 'contact', 'contactname']);

          return {
            id: crypto.randomUUID(),
            name,
            phone: formatPhone(
              getValue(row, [
                'phone',
                'phonenumber',
                'phone_number',
                'mobile',
                'mobilenumber',
                'tel',
                'telephone',
                'cell',
                'number',
              ]),
            ),
            unique_identifier:
              getValue(row, [
                'unique_identifier',
                'uniqueidentifier',
                'unique_id',
                'uniqueid',
                'identifier',
                'uid',
                'external_id',
                'externalid',
                'employee_id',
                'employeeid',
                'staff_id',
                'staffid',
              ]) || undefined,
            // An explicit `data.first_name` column wins; otherwise derive it
            // from the name column so template body vars have something to map.
            data: withDerivedFirstName(data, name),
            tags: [],
          };
        })
        .filter((row) => row.name || row.phone);

      if (parsedContacts.length === 0) {
        toast({
          variant: 'destructive',
          title: 'No contacts found',
          description: 'Make sure your file has columns named "name" and "phone".',
        });
        return;
      }

      setContacts(parsedContacts);
      setContactsError(null);
      toast({
        variant: 'success',
        title: 'Contacts imported',
        description: `${parsedContacts.length} contacts loaded from file.`,
      });
    } catch (error) {
      console.error('Failed to parse file', error);
      toast({
        variant: 'destructive',
        title: 'Failed to parse file',
        description: 'Please ensure the file is a valid Excel or CSV file.',
      });
    } finally {
      event.target.value = '';
    }
  };

  const handleContactChange = (id: string, field: keyof Contact, value: string) => {
    setContacts((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const next = { ...row, [field]: value };
        if (field === 'name' && !row._dataError) {
          next.data = withDerivedFirstName(row.data, value, row.name);
        }
        return next;
      }),
    );
    setContactsError(null);
  };

  const handleRemoveContact = (id: string) => {
    setContacts((prev) => prev.filter((row) => row.id !== id));
  };

  const handleAddManualContact = () => {
    setContacts((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: '',
        phone: '',
        unique_identifier: undefined,
        tags: [],
      },
    ]);
    setContactsError(null);
  };

  const onFormSubmit = handleSubmit(
    async (data) => {
      setContactsError(null);

      // Validate contacts
      if (contacts.length === 0) {
        setContactsError('At least one contact is required');
        document
          .getElementById('contacts-section')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const invalidContacts = contacts.filter((c) => !c.name.trim() || !c.phone?.trim());
      if (invalidContacts.length > 0) {
        setContactsError('All contacts must have both name and phone number');
        document
          .getElementById('contacts-section')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const contactsWithBadJson = contacts.filter((c) => c._dataError);
      if (contactsWithBadJson.length > 0) {
        setContactsError('Some contacts have invalid JSON data. Fix or clear the data fields.');
        document
          .getElementById('contacts-section')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      try {
        const contactsToSubmit = contacts.map(
          ({ id: _id, _dataRaw: _raw, _dataError: _err, ...rest }) => ({
            ...rest,
            data: rest.data && Object.keys(rest.data).length > 0 ? rest.data : undefined,
          }),
        );

        const selectedTemplate = approvedTemplates.find((t) => t.name === data.wa_template_name);
        const waMapping = {
          ...(data.wa_mapping_header_image && { header_image: data.wa_mapping_header_image }),
          body: (data.wa_mapping_body || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          ...(data.wa_mapping_button_url && { button_url: data.wa_mapping_button_url }),
        };

        const createDto: CreateCampaignDto = {
          name: data.name.trim(),
          configuration_id: data.configuration_id,
          channel: data.channel,
          ...(data.channel === 'PSTN' &&
            data.outbound_twilio_phone_number_id && {
              outbound_twilio_phone_number_id: data.outbound_twilio_phone_number_id,
            }),
          ...(data.channel === 'PSTN' &&
            data.outbound_telnyx_phone_number_id && {
              outbound_telnyx_phone_number_id: data.outbound_telnyx_phone_number_id,
            }),
          ...(data.workflow_id && {
            workflow_id: data.workflow_id,
          }),
          ...(data.outcome_source && { outcome_source: data.outcome_source }),
          ...(data.retryable_outcomes &&
            data.retryable_outcomes.length > 0 && {
              retryable_outcomes:
                data.retryable_outcomes as CreateCampaignDto['retryable_outcomes'],
            }),
          ...(data.channel === 'WHATSAPP' && {
            outbound_whatsapp_phone_number_id: data.outbound_whatsapp_phone_number_id || undefined,
            wa_template_name: data.wa_template_name,
            wa_template_language: selectedTemplate?.language || 'en_GB',
            wa_template_mapping: waMapping,
            wa_call_on_reply: !!data.wa_call_on_reply,
            wa_call_on_undelivered: !!data.wa_call_on_undelivered,
          }),

          contacts: contactsToSubmit,
          runs_at: data.runs_at,
        };

        await createMutation.mutateAsync(createDto);
        toast({
          variant: 'success',
          title: 'Campaign created',
          description: `"${data.name}" has been created successfully.`,
        });
        navigate('/campaigns');
      } catch (error: unknown) {
        const validationErrors = extractValidationErrors(error);
        if (validationErrors.length > 0) {
          mergeApiErrors(validationErrors, setError);
        } else {
          toast({
            variant: 'destructive',
            title: 'Failed to create campaign',
            description: extractErrorMessage(error),
          });
        }
      }
    },
    (formErrors) => {
      scrollToFirstError(formErrors);
    },
  );

  const handleCancel = () => {
    navigate('/campaigns');
  };

  if (configsLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onFormSubmit} className="space-y-8">
      <header className="space-y-2">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Campaigns</p>
        <h1 className="text-3xl font-bold tracking-tight">Create outreach campaign</h1>
        <p className="text-muted-foreground max-w-3xl">
          Pair an AI configuration with a contact list to launch hybrid agent campaigns. Upload
          Excel/CSV contacts (name + phone) then review entries before saving.
        </p>
      </header>
      <Card>
        <CardHeader>
          <CardTitle>Campaign details</CardTitle>
          <CardDescription>
            Give the campaign a name, select the config, and schedule when it should run.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Campaign name" htmlFor="name" error={errors.name?.message} required>
                <FormInput
                  id="name"
                  placeholder="e.g. London carers – Week 42"
                  autoComplete="off"
                  error={!!errors.name}
                  {...register('name')}
                />
              </FormField>

              <FormField label="Runs at" htmlFor="runs_at" error={errors.runs_at?.message} required>
                <FormInput
                  id="runs_at"
                  type="date"
                  error={!!errors.runs_at}
                  {...register('runs_at')}
                />
              </FormField>
            </div>

            <FormField
              label="Configuration"
              htmlFor="configuration_id"
              error={errors.configuration_id?.message}
              required
            >
              {configs.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No configurations available. Please create a configuration first.
                </p>
              ) : (
                <>
                  <FormSelect
                    id="configuration_id"
                    error={!!errors.configuration_id}
                    {...register('configuration_id')}
                  >
                    {configs.map((config) => {
                      const localStart = convertUTCToLocalTime(config.call_window_start);
                      const localEnd = convertUTCToLocalTime(config.call_window_end);
                      return (
                        <option key={config.id} value={config.id}>
                          {config.name} ({localStart}-{localEnd})
                        </option>
                      );
                    })}
                  </FormSelect>
                  {selectedConfig?.active_version && (
                    <p className="text-muted-foreground mt-1 text-xs">
                      Uses prompt: &quot;
                      {selectedConfig.active_version.prompt_template.slice(0, 80)}
                      {selectedConfig.active_version.prompt_template.length > 80 ? '…' : ''}&quot;
                    </p>
                  )}
                </>
              )}
            </FormField>

            {/* Channel */}
            <FormField label="Channel" htmlFor="channel">
              <FormSelect id="channel" {...register('channel')}>
                <option value="PSTN">Phone call (Twilio / Telnyx)</option>
                <option value="WHATSAPP">WhatsApp (template message)</option>
              </FormSelect>
              <p className="text-muted-foreground mt-1 text-xs">
                WhatsApp campaigns send an approved template to each contact; replies are handled by
                the AI text agent.
              </p>
            </FormField>

            {/* Provider-specific Number Selection (voice) */}
            {channel === 'PSTN' &&
              ((selectedConfig as { call_provider?: string } | undefined)?.call_provider ===
              'telnyx' ? (
                <FormField
                  label="Outbound Telnyx Number"
                  htmlFor="outbound_telnyx_phone_number_id"
                  error={errors.outbound_telnyx_phone_number_id?.message}
                >
                  <FormSelect
                    id="outbound_telnyx_phone_number_id"
                    error={!!errors.outbound_telnyx_phone_number_id}
                    {...register('outbound_telnyx_phone_number_id')}
                  >
                    <option value="">Use config/global default during activation</option>
                    {telnyxNumbers.map((number) => (
                      <option key={number.id} value={number.id}>
                        {number.friendly_name ? `${number.friendly_name} - ` : ''}
                        {number.phone_number}
                      </option>
                    ))}
                  </FormSelect>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Optional. You can also pick this when activating from the campaigns list.
                  </p>
                </FormField>
              ) : (
                <FormField
                  label="Outbound Twilio Number"
                  htmlFor="outbound_twilio_phone_number_id"
                  error={errors.outbound_twilio_phone_number_id?.message}
                >
                  <FormSelect
                    id="outbound_twilio_phone_number_id"
                    error={!!errors.outbound_twilio_phone_number_id}
                    {...register('outbound_twilio_phone_number_id')}
                  >
                    <option value="">Use config/global default during activation</option>
                    {twilioNumbers.map((number) => (
                      <option key={number.id} value={number.id}>
                        {number.friendly_name ? `${number.friendly_name} - ` : ''}
                        {number.phone_number}
                      </option>
                    ))}
                  </FormSelect>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Optional. You can also pick this when activating from the campaigns list.
                  </p>
                </FormField>
              ))}

            {/* WhatsApp campaign settings */}
            {channel === 'WHATSAPP' && (
              <div className="space-y-4 rounded-lg border border-dashed p-4">
                <FormField
                  label="WhatsApp sender number"
                  htmlFor="outbound_whatsapp_phone_number_id"
                >
                  <FormSelect
                    id="outbound_whatsapp_phone_number_id"
                    {...register('outbound_whatsapp_phone_number_id')}
                  >
                    <option value="">Use the configuration&apos;s inbound number</option>
                    {whatsappNumbers.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.display_phone_number || n.phone_number}
                      </option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormField
                  label="Template"
                  htmlFor="wa_template_name"
                  error={errors.wa_template_name?.message}
                  required
                >
                  {approvedTemplates.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      No approved templates yet. Create and approve one on the WhatsApp Templates
                      page first.
                    </p>
                  ) : (
                    <FormSelect
                      id="wa_template_name"
                      error={!!errors.wa_template_name}
                      {...register('wa_template_name')}
                    >
                      <option value="">Select a template…</option>
                      {approvedTemplates.map((t) => (
                        <option key={t.id} value={t.name}>
                          {t.name} ({t.language})
                        </option>
                      ))}
                    </FormSelect>
                  )}
                </FormField>

                <div className="grid gap-4 md:grid-cols-3">
                  <FormField label="Header image ← data key" htmlFor="wa_mapping_header_image">
                    <FormInput
                      id="wa_mapping_header_image"
                      placeholder="image_url"
                      {...register('wa_mapping_header_image')}
                    />
                  </FormField>
                  <FormField label="Body vars ← data keys (comma)" htmlFor="wa_mapping_body">
                    <FormInput
                      id="wa_mapping_body"
                      placeholder="vehicle, vdp_path"
                      {...register('wa_mapping_body')}
                    />
                  </FormField>
                  <FormField label="Button URL ← data key" htmlFor="wa_mapping_button_url">
                    <FormInput
                      id="wa_mapping_button_url"
                      placeholder="vdp_path"
                      {...register('wa_mapping_button_url')}
                    />
                  </FormField>
                </div>
                <p className="text-muted-foreground text-xs">
                  Each value is a key on the contact&apos;s Data JSON. Body keys fill {'{{1}}'},{' '}
                  {'{{2}}'}… in order; header image and button URL are optional.
                </p>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" {...register('wa_call_on_reply')} />
                  Call the contact on WhatsApp when they reply (subject to call permission)
                </label>

                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1" {...register('wa_call_on_undelivered')} />
                  <span>
                    Call the contact if the message never reaches them
                    <span className="text-muted-foreground block text-xs">
                      Covers a rejected send and a failed delivery — usually a number with no
                      WhatsApp. The call is placed with this campaign&apos;s configuration, within
                      its calling hours.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Campaign Review and Workflow Settings */}
            <FormField label="Default Review Config" htmlFor="default_review_config_id">
              <FormSelect id="default_review_config_id" {...register('default_review_config_id')}>
                <option value="">None (use system default)</option>
                {reviewConfigs.map((rc) => (
                  <option key={rc.id} value={rc.id}>
                    {rc.name}
                    {rc.is_default ? ' (System Default)' : ''}
                  </option>
                ))}
              </FormSelect>
              <p className="text-muted-foreground mt-1 text-xs">
                Review config used for auto-validation after each call in this campaign.
              </p>
            </FormField>

            <FormField label="Post-Call Workflow" htmlFor="workflow_id">
              <FormSelect id="workflow_id" {...register('workflow_id')}>
                <option value="">None (use review config default)</option>
                {workflows.map((wf) => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name}
                    {wf.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </FormSelect>
              <p className="text-muted-foreground mt-1 text-xs">
                Workflow to run after each call ends (email, SMS, conditions, etc.)
              </p>
            </FormField>

            {/* Retry behavior — prefilled from platform Campaign settings, editable here */}
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Outcome source" htmlFor="outcome_source">
                <FormSelect id="outcome_source" {...register('outcome_source')}>
                  <option value="REVIEWED">Reviewed (post-call verdict)</option>
                  <option value="RAW">Raw (outcome at call-end)</option>
                </FormSelect>
                <p className="text-muted-foreground mt-1 text-xs">
                  Which outcome the retry logic trusts when deciding whether to call again.
                </p>
              </FormField>

              <FormField label="Retryable outcomes" htmlFor="retryable_outcomes">
                <div className="grid grid-cols-2 gap-2">
                  {retryableOutcomeCandidates.map((outcome) => (
                    <label key={outcome} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={retryableOutcomes.includes(outcome)}
                        onChange={() => toggleRetryableOutcome(outcome)}
                      />
                      {outcome}
                    </label>
                  ))}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  A call ending with any checked outcome is retried (up to the config&apos;s retry
                  limit).
                </p>
              </FormField>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card id="contacts-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Contacts</CardTitle>
            <CardDescription>
              Upload Excel/CSV (columns: name, phone, unique_identifier) or add manually. The first
              word of each name is saved to data as <code>first_name</code> for template variables.
            </CardDescription>
          </div>
          <div className="flex gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
              Upload contacts
            </Button>
            <Button type="button" variant="outline" onClick={handleAddManualContact}>
              Add contact
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {contactsError && <FormError message={contactsError} />}

          {contacts.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No contacts uploaded yet. Use &quot;Upload contacts&quot; to import spreadsheets or
              &quot;Add contact&quot; to enter rows manually.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-muted-foreground text-left text-sm">
                    <th className="border-b px-3 py-2">Name</th>
                    <th className="border-b px-3 py-2">Phone number</th>
                    <th className="border-b px-3 py-2">Unique identifier</th>
                    <th className="border-b px-3 py-2">Data</th>
                    <th className="border-b px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <React.Fragment key={contact.id}>
                      <tr className="text-sm">
                        <td className="border-b px-3 py-2">
                          <input
                            value={contact.name}
                            onChange={(event) =>
                              handleContactChange(contact.id, 'name', event.target.value)
                            }
                            placeholder="Name"
                            className="border-input bg-background focus-visible:ring-primary w-full rounded-md border px-2 py-1 outline-none focus-visible:ring-2"
                          />
                        </td>
                        <td className="border-b px-3 py-2">
                          <input
                            value={contact.phone ?? ''}
                            onChange={(event) =>
                              handleContactChange(contact.id, 'phone', event.target.value)
                            }
                            placeholder="+44 0000 000000"
                            className="border-input bg-background focus-visible:ring-primary w-full rounded-md border px-2 py-1 outline-none focus-visible:ring-2"
                          />
                        </td>
                        <td className="border-b px-3 py-2">
                          <input
                            value={contact.unique_identifier || ''}
                            onChange={(event) =>
                              handleContactChange(
                                contact.id,
                                'unique_identifier',
                                event.target.value,
                              )
                            }
                            placeholder="Optional"
                            className="border-input bg-background focus-visible:ring-primary w-full rounded-md border px-2 py-1 outline-none focus-visible:ring-2"
                          />
                        </td>
                        <td className="border-b px-3 py-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => toggleDataEditor(contact.id)}
                          >
                            {expandedDataIds.has(contact.id) ? 'Hide' : 'Edit'}
                            {contact.data && Object.keys(contact.data).length > 0 && (
                              <span className="bg-primary text-primary-foreground ml-1 rounded-full px-1.5 text-xs">
                                {Object.keys(contact.data).length}
                              </span>
                            )}
                          </Button>
                        </td>
                        <td className="border-b px-3 py-2 text-right">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveContact(contact.id)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                      {expandedDataIds.has(contact.id) && (
                        <tr key={`${contact.id}-data`}>
                          <td colSpan={5} className="border-b px-3 py-2">
                            <textarea
                              className="border-input bg-background focus-visible:ring-primary w-full rounded-md border px-3 py-2 font-mono text-xs leading-relaxed outline-none focus-visible:ring-2"
                              rows={5}
                              value={
                                contact._dataRaw !== undefined
                                  ? contact._dataRaw
                                  : JSON.stringify(contact.data || {}, null, 2)
                              }
                              onChange={(e) => handleDataChange(contact.id, e.target.value)}
                              placeholder='{"key": "value"}'
                            />
                            {contact._dataError && (
                              <p className="mt-1 text-xs text-red-500">{contact._dataError}</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={configs.length === 0 || createMutation.isPending}>
          {createMutation.isPending ? 'Creating...' : 'Save campaign'}
        </Button>
      </div>
    </form>
  );
};
